import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import DateRangeFilter from '@/components/DateRangeFilter';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import Tooltiped from '@/components/Tooltiped';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useNetwork from '@/hooks/dq/useNetwork';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import {
  CoverTh,
  MessageTh,
  StyledTable,
} from '@/pages/EditorialDistribution/components/EditorialDistributionTable';
import EditorialDistributionTableRow from '@/pages/EditorialDistribution/components/EditorialDistributionTableRow';
import {
  EditorialFacebookPostResource,
  EditorialQueuePage,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostType, SortDirection } from '@/utils/enums';

import ClearFailedPostsButton from './ClearFailedPostsButton';
import CommentRepliedFilter from './CommentRepliedFilter';
import ContentTypeTableFilter from './ContentTypeTableFilter';
import MoreActions from './MoreActions';
import PageGuideButton from './PageGuideButton';
import PostsStatusNav from './PostsStatusNav';
import SortColumns from './SortColumns';

const Header = styled.div`
  display: flex;
  margin: 16px 0 24px;
  align-items: flex-end;
  justify-content: space-between;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.gray};
  margin-right: 12px;
`;

const TableActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;

  > * {
    margin: 0;
  }
`;

const StyledViewOptionsModal = styled(ViewOptionsModal)`
  margin: 0 !important;
`;

const StyledDateRangeFilter = styled(DateRangeFilter)`
  margin: 0 !important;

  border-color: ${({ theme }) => theme.colors.grayscale};
  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

export const tableColumns = [
  {
    title: 'Cover',
    toggable: false,
    defaultVisibility: true,
    sortable: false,
  },
  {
    title: 'Message',
    toggable: false,
    defaultVisibility: true,
    sortable: false,
  },
  {
    title: 'Network',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Page Name',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Content Type',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Status',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Benchmark Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Deletion Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Schedule Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Post Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Site Visits',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Site Visit Benchmark',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Reach Duration',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Departments',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Reactions',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Comments',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Shares',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Article Link',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Post Link',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Owner',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Notes',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Link Visits',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Post Comments',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Post Reactions',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Post Shares',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Post Clicks',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Post Impressions',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h CTR',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Engagement Rate',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '24h Video Views',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Link Visits',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Post Comments',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Post Reactions',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Post Shares',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Post Clicks',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Post Impressions',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h CTR',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Engagement Rate',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '1h Video Views',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Comments Checker Disabled',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Error Message',
    toggable: true,
    defaultVisibility: true,
  },
];

const sortableTableColumns = tableColumns.filter((col) => col.sortable ?? true);

const DEFAULT_SORT_STATE = {
  direction: SortDirection.DESC,
  property: 'publishedAt',
};

interface Props {
  search: string;
  activePage: EditorialQueuePage;
}

const PostsTable: FC<Props> = ({ search, activePage }) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);
  const { status } = useParams();
  const [network, networkLabel] = useNetwork();

  const [contentTypeFilter, setContentTypeFilter] =
    useState<EditorialFacebookPostType | null>(null);

  const [commentRepliedFilter, setCommentRepliedFilter] = useState('');

  const [dateRange, setDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const tableViewOptionsKey = `${status!}_${networkLabel}_dq_posts_table_v4`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const fetchPosts = ({ pageParam = '/v1/editorial/facebook-posts' }) => {
    return api.get<never, PaginatedResponse<EditorialFacebookPostResource>>(
      pageParam,
      {
        params: {
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
          ...(!!search && {
            'filter[search]': search,
          }),
          'filter[network]': network,
          'filter[queueStatus]': status === 'failed' ? '3' : '2',
          'filter[channelId]': activePage.id,
          'filter[excludePlaceholders]': 1,
          ...(status === 'deleted' && {
            'filter[deletedOnly]': 1,
          }),
          ...(!!dateRange.rangeStart && {
            'filter[dateFrom]': dateRange.rangeStart.toFormat(
              'yyyy-LL-dd 00:00:00',
            ),
          }),
          ...(!!dateRange.rangeEnd && {
            'filter[dateTo]': dateRange.rangeEnd.toFormat(
              'yyyy-LL-dd 23:59:59',
            ),
          }),
          ...(!!contentTypeFilter && {
            'filter[type]': contentTypeFilter,
          }),
          ...(!!commentRepliedFilter && {
            'filter[commentReplies]': commentRepliedFilter,
          }),
          include: `departments${status === 'posted' ? ',stats' : ''}`,
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<EditorialFacebookPostResource>>(
    [
      'editorial_distribution_schedules',
      {
        network,
        queueable: true,
        status,
        search,
        sort,
        selectedChannels: activePage.id.toString(),
        excludePlaceholders: true,
        contentTypeFilter,
        dateRange,
        commentRepliedFilter,
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      refetchInterval: 60 * 1000,
    },
  );

  const schedules = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<EditorialFacebookPostResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  const [columns, setColumns] = useState(() => {
    const stored = localStorage.getItem(tableViewOptionsKey);

    if (!stored) {
      return sortableTableColumns.map((col) => col.title);
    }

    return JSON.parse(stored) as string[];
  });

  const renderColumns = () => {
    return columns.map((column) => {
      switch (column) {
        case 'Network':
          return (
            getColumnVisibility('Network') && (
              <Fragment key={column}>
                <Th>Network</Th>
              </Fragment>
            )
          );

        case 'Page Name':
          return (
            getColumnVisibility('Page Name') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="channelName"
                  title="Page Name"
                />
              </Fragment>
            )
          );

        case 'Content Type':
          return (
            getColumnVisibility('Content Type') && (
              <Fragment key={column}>
                <Th>Content Type</Th>
              </Fragment>
            )
          );

        case 'Status':
          return (
            getColumnVisibility('Status') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="status"
                  title="Status"
                />
              </Fragment>
            )
          );

        case 'Benchmark Reach':
          return (
            getColumnVisibility('Benchmark Reach') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="benchmarkReach"
                  title="Benchmark Reach"
                />
              </Fragment>
            )
          );

        case 'Deletion Date':
          return (
            getColumnVisibility('Deletion Date') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="deletedAt"
                  title="Deletion Date"
                />
              </Fragment>
            )
          );

        case 'Schedule Date':
          return (
            getColumnVisibility('Schedule Date') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="createdAt"
                  title="Schedule Date"
                />
              </Fragment>
            )
          );

        case 'Post Date':
          return (
            getColumnVisibility('Post Date') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="publishedAt"
                  title="Post Date"
                />
              </Fragment>
            )
          );

        case 'Site Visits':
          return (
            getColumnVisibility('Site Visits') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisits"
                  title="Site Visits"
                />
              </Fragment>
            )
          );

        case 'Site Visit Benchmark':
          return (
            getColumnVisibility('Site Visit Benchmark') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisitBenchmark"
                  title="Site Visit Benchmark"
                />
              </Fragment>
            )
          );

        case 'Reach Duration':
          return (
            getColumnVisibility('Reach Duration') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reachDuration"
                  title="Reach Duration"
                />
              </Fragment>
            )
          );

        case 'Departments':
          return (
            getColumnVisibility('Departments') && (
              <Fragment key={column}>
                <Th>Departments</Th>
              </Fragment>
            )
          );

        case 'Reactions':
          return (
            getColumnVisibility('Reactions') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reactions"
                  title="Reactions"
                />
              </Fragment>
            )
          );

        case 'Comments':
          return (
            getColumnVisibility('Comments') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="comments"
                  title="Comments"
                />
              </Fragment>
            )
          );

        case 'Shares':
          return (
            getColumnVisibility('Shares') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="shares"
                  title="Shares"
                />
              </Fragment>
            )
          );

        case 'Article Link':
          return (
            getColumnVisibility('Article Link') && (
              <Fragment key={column}>
                <Th>Article Link</Th>
              </Fragment>
            )
          );

        case 'Post Link':
          return (
            getColumnVisibility('Post Link') && (
              <Fragment key={column}>
                <Th>Post Link</Th>
              </Fragment>
            )
          );

        case 'Owner':
          return (
            getColumnVisibility('Owner') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="owner"
                  title="Owner"
                />
              </Fragment>
            )
          );

        case 'Notes':
          return (
            getColumnVisibility('Notes') && (
              <Fragment key={column}>
                <Th>Notes</Th>
              </Fragment>
            )
          );

        case '24h Link Visits':
          return (
            getColumnVisibility('24h Link Visits') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_link_visits"
                  title="24h Link Visits"
                />
              </Fragment>
            )
          );

        case '24h Post Comments':
          return (
            getColumnVisibility('24h Post Comments') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_comments"
                  title="24h Post Comments"
                />
              </Fragment>
            )
          );

        case '24h Post Reactions':
          return (
            getColumnVisibility('24h Post Reactions') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_reactions"
                  title="24h Post Reactions"
                />
              </Fragment>
            )
          );

        case '24h Post Shares':
          return (
            getColumnVisibility('24h Post Shares') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_shares"
                  title="24h Post Shares"
                />
              </Fragment>
            )
          );

        case '24h Post Clicks':
          return (
            getColumnVisibility('24h Post Clicks') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_clicks"
                  title="24h Post Clicks"
                />
              </Fragment>
            )
          );

        case '24h Post Impressions':
          return (
            getColumnVisibility('24h Post Impressions') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_impressions"
                  title="24h Post Impressions"
                />
              </Fragment>
            )
          );

        case '24h CTR':
          return (
            getColumnVisibility('24h CTR') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_ctr"
                  title="24h CTR"
                />
              </Fragment>
            )
          );

        case '24h Engagement Rate':
          return (
            getColumnVisibility('24h Engagement Rate') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_engagement_rate"
                  title="24h Engagement Rate"
                />
              </Fragment>
            )
          );
        case '24h Video Views':
          return (
            getColumnVisibility('24h Video Views') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_video_views"
                  title="24h Video Views"
                />
              </Fragment>
            )
          );

        case '1h Link Visits':
          return (
            getColumnVisibility('1h Link Visits') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_link_visits"
                  title="1h Link Visits"
                />
              </Fragment>
            )
          );

        case '1h Post Comments':
          return (
            getColumnVisibility('1h Post Comments') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_comments"
                  title="1h Post Comments"
                />
              </Fragment>
            )
          );

        case '1h Post Reactions':
          return (
            getColumnVisibility('1h Post Reactions') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_reactions"
                  title="1h Post Reactions"
                />
              </Fragment>
            )
          );

        case '1h Post Shares':
          return (
            getColumnVisibility('1h Post Shares') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_shares"
                  title="1h Post Shares"
                />
              </Fragment>
            )
          );

        case '1h Post Clicks':
          return (
            getColumnVisibility('1h Post Clicks') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_clicks"
                  title="1h Post Clicks"
                />
              </Fragment>
            )
          );

        case '1h Post Impressions':
          return (
            getColumnVisibility('1h Post Impressions') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_impressions"
                  title="1h Post Impressions"
                />
              </Fragment>
            )
          );

        case '1h CTR':
          return (
            getColumnVisibility('1h CTR') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_ctr"
                  title="1h CTR"
                />
              </Fragment>
            )
          );

        case '1h Engagement Rate':
          return (
            getColumnVisibility('1h Engagement Rate') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_engagement_rate"
                  title="1h Engagement Rate"
                />
              </Fragment>
            )
          );
        case '1h Video Views':
          return (
            getColumnVisibility('1h Video Views') && (
              <Fragment key={column}>
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_video_views"
                  title="1h Video Views"
                />
              </Fragment>
            )
          );
        case 'Comments Checker Disabled':
          return (
            getColumnVisibility('Comments Checker Disabled') && (
              <Fragment key={column}>
                <Th>Comments Checker Disabled</Th>
              </Fragment>
            )
          );

        case 'Error Message':
          return (
            getColumnVisibility('Error Message') && (
              <Fragment key={column}>
                <Th>Error Message</Th>
              </Fragment>
            )
          );

        default:
          return null;
      }
    });
  };

  useEffect(() => {
    setContentTypeFilter(null);
    setDateRange({});
    setCommentRepliedFilter('');
  }, [status]);

  useEffect(() => {
    localStorage.setItem(tableViewOptionsKey, JSON.stringify(columns));
  }, [columns, tableViewOptionsKey]);

  return (
    <>
      <Header>
        <PostsStatusNav />
        <TableActions>
          {status === 'posted' && (
            <SortColumns columns={columns} setColumns={setColumns} />
          )}
          {status === 'failed' && (
            <ClearFailedPostsButton
              channelId={activePage.id}
              hasFailedPosts={!!schedules?.length}
            />
          )}
          {/* Comments status filter */}
          {status === 'posted' && (
            <CommentRepliedFilter
              value={commentRepliedFilter}
              setValue={setCommentRepliedFilter}
            />
          )}
          {/* Published at filter */}
          <Tooltiped title="Date Range">
            <StyledDateRangeFilter
              text=""
              allDaysSelectable
              selectedDateRange={dateRange}
              setSelectedDateRange={setDateRange}
            />
          </Tooltiped>
          {/* Content type filter */}
          <ContentTypeTableFilter
            contentType={contentTypeFilter}
            setContentType={setContentTypeFilter}
          />
          <StyledViewOptionsModal
            columns={tableColumns}
            tableKey={tableViewOptionsKey}
          />
          <PageGuideButton activePage={activePage} />
          <MoreActions activePage={activePage} />
        </TableActions>
      </Header>
      <Heading $color="gray" $type="smallHeadline" as="div">
        <Badge as="div" $type="extraSmall" $color="white">
          {data?.pages?.[0].meta.total ?? 0}
        </Badge>
        {activePage.name}
        {status === 'deleted' && ' · DELETED'}
      </Heading>
      <ScrollableTable>
        <StyledTable $stickyActions={status !== 'deleted'}>
          <thead>
            <tr>
              {getColumnVisibility('Cover') && <CoverTh>Cover</CoverTh>}
              {getColumnVisibility('Message') && <MessageTh>Message</MessageTh>}
              {renderColumns()}
              {/* 
              {getColumnVisibility('Network') && <Th>Network</Th>}
              {getColumnVisibility('Page Name') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="channelName"
                  title="Page Name"
                />
              )}
              {getColumnVisibility('Content Type') && <Th>Content Type</Th>}
              {getColumnVisibility('Status') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="status"
                  title="Status"
                />
              )}
              {getColumnVisibility('Benchmark Reach') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="benchmarkReach"
                  title="Benchmark Reach"
                />
              )}
              {getColumnVisibility('Deletion Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="deletedAt"
                  title="Deletion Date"
                />
              )}
              {getColumnVisibility('Schedule Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="createdAt"
                  title="Schedule Date"
                />
              )}
              {getColumnVisibility('Post Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="publishedAt"
                  title="Post Date"
                />
              )}
              {getColumnVisibility('Site Visits') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisits"
                  title="Site Visits"
                />
              )}
              {getColumnVisibility('Site Visit Benchmark') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisitBenchmark"
                  title="Site Visit Benchmark"
                />
              )}
              {getColumnVisibility('Reach Duration') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reachDuration"
                  title="Reach Duration"
                />
              )}
              {getColumnVisibility('Departments') && <Th>Departments</Th>}
              {getColumnVisibility('Reactions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reactions"
                  title="Reactions"
                />
              )}
              {getColumnVisibility('Comments') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="comments"
                  title="Comments"
                />
              )}
              {getColumnVisibility('Shares') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="shares"
                  title="Shares"
                />
              )}
              {getColumnVisibility('Article Link') && <Th>Article Link</Th>}
              {getColumnVisibility('Post Link') && <Th>Post Link</Th>}
              {getColumnVisibility('Owner') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="owner"
                  title="Owner"
                />
              )}
              {getColumnVisibility('Notes') && <Th>Notes</Th>}
              {getColumnVisibility('24h Link Visits') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_link_visits"
                  title="24h Link Visits"
                />
              )}
              {getColumnVisibility('24h Post Comments') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_comments"
                  title="24h Post Comments"
                />
              )}
              {getColumnVisibility('24h Post Reactions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_reactions"
                  title="24h Post Reactions"
                />
              )}
              {getColumnVisibility('24h Post Shares') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_shares"
                  title="24h Post Shares"
                />
              )}
              {getColumnVisibility('24h Post Clicks') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_clicks"
                  title="24h Post Clicks"
                />
              )}
              {getColumnVisibility('24h Post Impressions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_post_impressions"
                  title="24h Post Impressions"
                />
              )}
              {getColumnVisibility('24h CTR') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_ctr"
                  title="24h CTR"
                />
              )}
              {getColumnVisibility('24h Engagement Rate') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="24h_engagement_rate"
                  title="24h Engagement Rate"
                />
              )}
              {getColumnVisibility('1h Link Visits') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_link_visits"
                  title="1h Link Visits"
                />
              )}
              {getColumnVisibility('1h Post Comments') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_comments"
                  title="1h Post Comments"
                />
              )}
              {getColumnVisibility('1h Post Reactions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_reactions"
                  title="1h Post Reactions"
                />
              )}
              {getColumnVisibility('1h Post Shares') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_shares"
                  title="1h Post Shares"
                />
              )}
              {getColumnVisibility('1h Post Clicks') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_clicks"
                  title="1h Post Clicks"
                />
              )}
              {getColumnVisibility('1h Post Impressions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_post_impressions"
                  title="1h Post Impressions"
                />
              )}
              {getColumnVisibility('1h CTR') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_ctr"
                  title="1h CTR"
                />
              )}
              {getColumnVisibility('1h Engagement Rate') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="1h_engagement_rate"
                  title="1h Engagement Rate"
                />
              )}
              {getColumnVisibility('Error Message') && <Th>Error Message</Th>} */}

              {status !== 'deleted' && (
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!schedules?.length}
            >
              {schedules.map((schedule) => (
                <EditorialDistributionTableRow
                  columns={columns}
                  key={schedule.id}
                  schedule={schedule}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default PostsTable;
